import type { BusinessType, CostCategory } from '@buildbee/contracts';

/**
 * CSI MasterFormat (2018 edition) cost code seed. Codes use the six-digit "DD SS SS" form.
 * Division-level codes ("09 00 00") roll up every section in the division.
 */

export type CsiDivision = { division: string; name: string };
export type CostCodeSeed = {
  code: string;
  name: string;
  division: string;
  divisionName: string;
  level: 'division' | 'section';
};

export const CSI_DIVISIONS: readonly CsiDivision[] = [
  { division: '00', name: 'Procurement and Contracting Requirements' },
  { division: '01', name: 'General Requirements' },
  { division: '02', name: 'Existing Conditions' },
  { division: '03', name: 'Concrete' },
  { division: '04', name: 'Masonry' },
  { division: '05', name: 'Metals' },
  { division: '06', name: 'Wood, Plastics, and Composites' },
  { division: '07', name: 'Thermal and Moisture Protection' },
  { division: '08', name: 'Openings' },
  { division: '09', name: 'Finishes' },
  { division: '10', name: 'Specialties' },
  { division: '11', name: 'Equipment' },
  { division: '12', name: 'Furnishings' },
  { division: '13', name: 'Special Construction' },
  { division: '14', name: 'Conveying Equipment' },
  { division: '21', name: 'Fire Suppression' },
  { division: '22', name: 'Plumbing' },
  { division: '23', name: 'Heating, Ventilating, and Air Conditioning (HVAC)' },
  { division: '25', name: 'Integrated Automation' },
  { division: '26', name: 'Electrical' },
  { division: '27', name: 'Communications' },
  { division: '28', name: 'Electronic Safety and Security' },
  { division: '31', name: 'Earthwork' },
  { division: '32', name: 'Exterior Improvements' },
  { division: '33', name: 'Utilities' },
];

/** Common level-2 sections, keyed by code. */
const SECTIONS: readonly [code: string, name: string][] = [
  ['01 10 00', 'Summary of Work'],
  ['01 21 00', 'Allowances'],
  ['01 31 00', 'Project Management and Coordination'],
  ['01 41 00', 'Regulatory Requirements and Permits'],
  ['01 45 00', 'Quality Control and Testing'],
  ['01 50 00', 'Temporary Facilities and Controls'],
  ['01 56 00', 'Temporary Barriers and Enclosures'],
  ['01 74 00', 'Cleaning and Waste Management'],
  ['01 77 00', 'Closeout Procedures'],
  ['02 21 00', 'Surveys'],
  ['02 41 00', 'Demolition'],
  ['02 82 00', 'Asbestos Remediation'],
  ['03 10 00', 'Concrete Forming and Accessories'],
  ['03 20 00', 'Concrete Reinforcing'],
  ['03 30 00', 'Cast-in-Place Concrete'],
  ['03 35 00', 'Concrete Finishing'],
  ['04 20 00', 'Unit Masonry'],
  ['04 40 00', 'Stone Assemblies'],
  ['05 12 00', 'Structural Steel Framing'],
  ['05 40 00', 'Cold-Formed Metal Framing'],
  ['05 50 00', 'Metal Fabrications'],
  ['05 52 00', 'Metal Railings'],
  ['06 10 00', 'Rough Carpentry'],
  ['06 17 00', 'Shop-Fabricated Structural Wood'],
  ['06 20 00', 'Finish Carpentry'],
  ['06 41 00', 'Architectural Wood Casework'],
  ['07 10 00', 'Dampproofing and Waterproofing'],
  ['07 21 00', 'Thermal Insulation'],
  ['07 25 00', 'Weather Barriers'],
  ['07 31 00', 'Shingles and Shakes'],
  ['07 46 00', 'Siding'],
  ['07 50 00', 'Membrane Roofing'],
  ['07 62 00', 'Sheet Metal Flashing and Trim'],
  ['07 84 00', 'Firestopping'],
  ['07 92 00', 'Joint Sealants'],
  ['08 11 00', 'Metal Doors and Frames'],
  ['08 14 00', 'Wood Doors'],
  ['08 36 00', 'Panel Doors'],
  ['08 41 00', 'Entrances and Storefronts'],
  ['08 50 00', 'Windows'],
  ['08 71 00', 'Door Hardware'],
  ['08 80 00', 'Glazing'],
  ['09 21 16', 'Gypsum Board Assemblies'],
  ['09 22 16', 'Non-Structural Metal Framing'],
  ['09 30 00', 'Tiling'],
  ['09 51 00', 'Acoustical Ceilings'],
  ['09 64 00', 'Wood Flooring'],
  ['09 65 00', 'Resilient Flooring'],
  ['09 68 00', 'Carpeting'],
  ['09 91 00', 'Painting'],
  ['10 14 00', 'Signage'],
  ['10 21 00', 'Compartments and Cubicles'],
  ['10 28 00', 'Toilet, Bath, and Laundry Accessories'],
  ['10 44 00', 'Fire Protection Specialties'],
  ['11 31 00', 'Residential Appliances'],
  ['11 40 00', 'Foodservice Equipment'],
  ['12 21 00', 'Window Blinds'],
  ['12 36 00', 'Countertops'],
  ['13 34 00', 'Fabricated Engineered Structures'],
  ['14 21 00', 'Electric Traction Elevators'],
  ['14 24 00', 'Hydraulic Elevators'],
  ['21 13 00', 'Fire-Suppression Sprinkler Systems'],
  ['21 30 00', 'Fire Pumps'],
  ['22 05 00', 'Common Work Results for Plumbing'],
  ['22 10 00', 'Plumbing Piping'],
  ['22 30 00', 'Plumbing Equipment'],
  ['22 40 00', 'Plumbing Fixtures'],
  ['23 05 00', 'Common Work Results for HVAC'],
  ['23 30 00', 'HVAC Air Distribution'],
  ['23 70 00', 'Central HVAC Equipment'],
  ['23 80 00', 'Decentralized HVAC Equipment'],
  ['25 50 00', 'Integrated Automation Facility Controls'],
  ['26 05 00', 'Common Work Results for Electrical'],
  ['26 20 00', 'Low-Voltage Electrical Transmission'],
  ['26 24 00', 'Switchboards and Panelboards'],
  ['26 50 00', 'Lighting'],
  ['27 10 00', 'Structured Cabling'],
  ['27 41 00', 'Audio-Video Systems'],
  ['28 10 00', 'Access Control'],
  ['28 20 00', 'Video Surveillance'],
  ['28 46 00', 'Fire Detection and Alarm'],
  ['31 10 00', 'Site Clearing'],
  ['31 20 00', 'Earth Moving'],
  ['31 23 00', 'Excavation and Fill'],
  ['31 60 00', 'Special Foundations and Load-Bearing Elements'],
  ['32 12 00', 'Flexible Paving'],
  ['32 13 00', 'Rigid Paving'],
  ['32 31 00', 'Fences and Gates'],
  ['32 90 00', 'Planting'],
  ['33 10 00', 'Water Utilities'],
  ['33 30 00', 'Sanitary Sewerage Utilities'],
  ['33 40 00', 'Stormwater Utilities'],
];

/** Divisions offered by default for each business type. */
const DIVISIONS_BY_BUSINESS_TYPE: Record<BusinessType, readonly string[]> = {
  residential: ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12', '22', '23', '26', '27', '31', '32', '33'],
  remodeler: ['01', '02', '06', '07', '08', '09', '10', '11', '12', '22', '23', '26'],
  commercial: CSI_DIVISIONS.map((d) => d.division),
  specialty_sub: ['01', '21', '22', '23', '25', '26', '27', '28'],
};

const divisionName = (division: string): string =>
  CSI_DIVISIONS.find((d) => d.division === division)?.name ?? `Division ${division}`;

/** Two-digit division of a cost code ("09 21 16" -> "09", "09-000" -> "09", "100" -> null). */
export function divisionOf(code: string): string | null {
  const m = /^(\d{2})(?:[\s.-]|\d{4}$|$)/.exec(code.trim());
  return m ? m[1]! : null;
}

/** Canonical form for comparing codes typed in different styles ("09-21-16", "092116"). */
export function normalizeCostCode(code: string): string {
  const trimmed = code.trim().toUpperCase();
  const digits = trimmed.replace(/[\s.-]/g, '');
  if (/^\d{6}$/.test(digits)) return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 6)}`;
  return trimmed.replace(/\s+/g, ' ');
}

/** The full MasterFormat seed (all divisions and sections), ordered by code. */
export function masterFormatLibrary(): CostCodeSeed[] {
  const out: CostCodeSeed[] = [];
  for (const d of CSI_DIVISIONS) {
    out.push({ code: `${d.division} 00 00`, name: d.name, division: d.division, divisionName: d.name, level: 'division' });
    for (const [code, name] of SECTIONS) {
      if (code.startsWith(`${d.division} `)) {
        out.push({ code, name, division: d.division, divisionName: d.name, level: 'section' });
      }
    }
  }
  return out;
}

/** Cost code seed for a business type (subset of divisions relevant to that kind of company). */
export function costCodeSeed(businessType: BusinessType): CostCodeSeed[] {
  const divisions = new Set(DIVISIONS_BY_BUSINESS_TYPE[businessType]);
  return masterFormatLibrary().filter((c) => divisions.has(c.division));
}

export type CostCodeEntry = CostCodeSeed & {
  source: 'custom' | 'csi' | 'template' | 'in_use';
  /** Workspace `cost_code` row id (custom codes only). */
  id: string | null;
  category: CostCategory | null;
  description: string | null;
};

/** A workspace's own cost code (`cost_code` row). */
export type CustomCostCode = {
  id: string;
  code: string;
  title: string;
  division?: string | null;
  category?: CostCategory | null;
  description?: string | null;
};

/**
 * Merge the workspace's own codes with the business-type seed, codes from project templates and
 * codes already used on budget lines. The first occurrence of a normalized code wins (custom,
 * then seed, then templates, then in use), so a workspace can rename a seed code.
 */
export function mergeCostCodeLibrary(
  seed: CostCodeSeed[],
  templateCodes: { code: string; name: string }[],
  inUse: { code: string; name: string }[],
  custom: CustomCostCode[] = [],
): CostCodeEntry[] {
  const seen = new Map<string, CostCodeEntry>();
  const add = (
    c: { code: string; name: string; level?: 'division' | 'section'; division?: string | null },
    source: CostCodeEntry['source'],
    extra: Pick<CostCodeEntry, 'id' | 'category' | 'description'> = { id: null, category: null, description: null },
  ) => {
    const code = normalizeCostCode(c.code);
    if (!code || seen.has(code)) return;
    const division = c.division || divisionOf(code) || '';
    seen.set(code, {
      code,
      name: c.name.trim() || code,
      division,
      divisionName: division ? divisionName(division) : 'Other',
      level: c.level ?? 'section',
      source,
      ...extra,
    });
  };
  for (const c of custom) {
    add({ code: c.code, name: c.title, division: c.division }, 'custom', {
      id: c.id,
      category: c.category ?? null,
      description: c.description ?? null,
    });
  }
  for (const c of seed) add(c, 'csi');
  for (const c of templateCodes) add(c, 'template');
  for (const c of inUse) add(c, 'in_use');
  return [...seen.values()].sort((a, b) => a.code.localeCompare(b.code, 'en', { numeric: true }));
}

/**
 * Validate a workspace cost code before saving: normalized code, division (explicit or derived from
 * a MasterFormat-style code) and a list of problems (empty when valid).
 */
export function prepareCustomCostCode(input: { code: string; division?: string | null }): { code: string; division: string | null; issues: string[] } {
  const code = normalizeCostCode(input.code);
  const issues: string[] = [];
  if (!code) issues.push('Enter a cost code');
  if (code.length > 40) issues.push('Cost codes are at most 40 characters');
  if (!/^[A-Z0-9][A-Z0-9 .\-_/]*$/.test(code)) issues.push('Use letters, digits, spaces, dots, dashes, underscores or slashes');
  const derived = divisionOf(code);
  const division = input.division ?? derived;
  if (input.division && derived && input.division !== derived) issues.push(`Code ${code} belongs to division ${derived}, not ${input.division}`);
  return { code, division: division ?? null, issues };
}
